import { useState } from 'react';

import FileUploadIcon from '@mui/icons-material/FileUpload';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tab from '@mui/material/Tab';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  FILE_MANAGER_UPLOAD_TYPE,
  FILE_SIZE,
  MAX_FILE_MANAGER_NUMBER,
  MAX_FILE_NUMBER,
} from '../../../../constants';
import FileUploadInput from '../../../elements/file-upload-input/FileUploadInput';
import Text from '../../../elements/text/Text';
import Modal from '../../../sections/modal/Modal';
import ModalButtons from '../../../sections/modal/ModalButtons';
import { MIME_TYPE } from '../constants';

const UploadButton = ({ action, loading }) => {
  const { t } = useTranslation();

  const [files, setFiles] = useState([]);
  const [modal, setModal] = useState(false);
  const [tab, setTab] = useState(FILE_MANAGER_UPLOAD_TYPE.REG);

  const toggleModal = () => {
    setModal(!modal);
    setFiles([]);
  };

  const handleSubmit = () => {
    // The tab determines the type. You can't check if there is a zip file or not do determine
    // which action to take because a user may wish to simply upload a zip file, without unzipping it.
    // In that case, we simply pass in the type based on the tab displayed.
    action(files, tab);
    toggleModal();
  };

  const handleTabChange = (e, newValue) => {
    setFiles([]);
    setTab(newValue);
  };

  return (
    <>
      <Button
        startIcon={<FileUploadIcon />}
        onClick={toggleModal}
        size="small"
        variant="text"
        color="inherit"
        disabled={loading}
      >
        {t('buttons.upload', { ns: 'fileBrowser' })}
      </Button>
      <Modal
        action={
          <ModalButtons
            actionProps={{ disabled: files.length === 0 }}
            actionText={t('upload', { ns: 'buttons' })}
            onCancel={toggleModal}
            onAction={handleSubmit}
          />
        }
        openModal={modal}
        title={t('fileUpload', { ns: 'instance' })}
        handleClose={toggleModal}
      >
        <Text>{t('descriptions.upload', { ns: 'fileBrowser' })}</Text>
        <TabContext value={tab}>
          <TabList
            onChange={handleTabChange}
            variant="fullWidth"
            textColor="primary"
            indicatorColor="primary"
            style={{ marginBottom: '1em' }}
          >
            <Tab
              label={t('uploadFile', { ns: 'fileBrowser' })}
              value={FILE_MANAGER_UPLOAD_TYPE.REG}
            />
            <Tab
              label={t('uploadUnzip', { ns: 'fileBrowser' })}
              value={FILE_MANAGER_UPLOAD_TYPE.ZIP}
            />
          </TabList>
          <Box>
            <TabPanel
              value={FILE_MANAGER_UPLOAD_TYPE.REG}
              style={{ padding: 0 }}
            >
              <FileUploadInput
                countLimit={MAX_FILE_MANAGER_NUMBER}
                fileList={files}
                instructionText={t('upload.drop20', { ns: 'fields' })}
                onSetFiles={setFiles}
                sizeLimit={FILE_SIZE.BYTES}
              />
            </TabPanel>
            <TabPanel
              value={FILE_MANAGER_UPLOAD_TYPE.ZIP}
              style={{ padding: 0 }}
            >
              <FileUploadInput
                countLimit={MAX_FILE_NUMBER}
                fileList={files}
                instructionText={t('upload.dropZip', { ns: 'fields' })}
                onSetFiles={setFiles}
                sizeLimit={FILE_SIZE.BYTES}
                typeLimit={[
                  MIME_TYPE.zip,
                  MIME_TYPE['7z'],
                  MIME_TYPE['window-zip'],
                ]}
              />
            </TabPanel>
          </Box>
        </TabContext>
      </Modal>
    </>
  );
};

UploadButton.propTypes = {
  action: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default UploadButton;
